import { c as _c } from "react/compiler-runtime";
// «Мои образы» — личные подборки покупателя (fd-discovery-04). Раздел кабинета
// в том же каркасе, что «История просмотра»: серая панель, сетка карточек.
//
//   /profile/looks      — список образов и «Создать образ»;
//   /profile/looks/:id  — образ: товары (карточки «Истории»), убрать товар,
//                         переименовать, «Доступ по ссылке» + копировать
//                         ссылку, удалить.
// Товары добавляются из карточки товара кнопкой «В подборку».
import { Skeleton } from "@osmix/ui/skeleton";
import { useQuery } from "@tanstack/react-query";
import { Layers } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { api, type Look } from "@/api";
import { EditIcon, PlusIcon, ShareIcon, TrashIcon, XIcon } from "@/components/icons";
import { LookNameDialog, productCountLabel, useLookCache, useLooks } from "@/components/looks/look-dialogs";
import { HistoryCard, type HistoryItem } from "@/components/profile/history-card";
import { ProfilePanel } from "@/components/profile/profile-shell";
import { CabinetEmpty } from "@/components/profile/return-parts";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useProfile } from "@/store";
const GRID = "grid grid-cols-2 gap-x-2 gap-y-4 sm:grid-cols-3 sm:gap-x-4 md:grid-cols-4 lg:grid-cols-5";
const lookUrl = (look: Look) => `${window.location.origin}/feed/${look.slug}`;
export function ProfileLooksPage() {
  const $ = _c(18);
  const looks = useLooks();
  const refresh = useLookCache();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  let t0;
  if ($[0] !== looks.data) {
    t0 = looks.data ?? [];
    $[0] = looks.data;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const list = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setCreating(true);
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Button size="sm" variant="dark" onClick={t1} data-testid="look-create"><PlusIcon size={16} />Создать образ</Button>;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== list || $[5] !== looks.isPending) {
    t3 = looks.isPending ? <div className={GRID}>{Array.from({
        length: 5
      }, _temp)}</div> : list.length === 0 ? <CabinetEmpty title={"\u041E\u0431\u0440\u0430\u0437\u043E\u0432 \u043F\u043E\u043A\u0430 \u043D\u0435\u0442"} hint={"\u0421\u043E\u0431\u0438\u0440\u0430\u0439\u0442\u0435 \u0442\u043E\u0432\u0430\u0440\u044B \u0432 \u043E\u0431\u0440\u0430\u0437\u044B: \u043D\u0430 \u043A\u0430\u0440\u0442\u043E\u0447\u043A\u0435 \u0442\u043E\u0432\u0430\u0440\u0430 \u043D\u0430\u0436\u043C\u0438\u0442\u0435 \xAB\u0412 \u043F\u043E\u0434\u0431\u043E\u0440\u043A\u0443\xBB \u0438\u043B\u0438 \u0441\u043E\u0437\u0434\u0430\u0439\u0442\u0435 \u043E\u0431\u0440\u0430\u0437 \u0437\u0434\u0435\u0441\u044C."} /> : <div className={GRID} data-testid="looks-grid">{list.map(_temp2)}</div>;
    $[4] = list;
    $[5] = looks.isPending;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = creating ? "open" : "closed";
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setCreating(false);
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== navigate || $[9] !== refresh) {
    t6 = async title => {
      const look_0 = await api.looks.create({
        title
      });
      refresh(look_0);
      navigate(`/profile/looks/${encodeURIComponent(look_0.id)}`);
    };
    $[8] = navigate;
    $[9] = refresh;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== creating || $[12] !== t4 || $[13] !== t6) {
    t7 = <LookNameDialog key={t4} open={creating} onClose={t5} title={"\u041D\u043E\u0432\u044B\u0439 \u043E\u0431\u0440\u0430\u0437"} submitLabel={"\u0421\u043E\u0437\u0434\u0430\u0442\u044C"} onSubmit={t6} />;
    $[11] = creating;
    $[12] = t4;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t3 || $[16] !== t7) {
    t8 = <ProfilePanel title={"\u041C\u043E\u0438 \u043E\u0431\u0440\u0430\u0437\u044B"} actions={t2}>{t3}{t7}</ProfilePanel>;
    $[15] = t3;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
}
function _temp2(look) {
  return <LookCard key={look.id} look={look} />;
}
function _temp(_, i) {
  return <Skeleton key={i} className="aspect-[3/4] rounded-[12px]" />;
}
function LookCard(t0) {
  const $ = _c(20);
  const {
    look
  } = t0;
  const t1 = `/profile/looks/${encodeURIComponent(look.id)}`;
  let t2;
  if ($[0] !== look.cover) {
    t2 = look.cover ? <img src={look.cover} alt="" className="size-full object-cover transition-transform duration-300 group-hover:scale-[1.02]" /> : <span className="grid size-full place-items-center text-ds-tertiary"><Layers className="size-8" strokeWidth={1.5} aria-hidden="true" /></span>;
    $[0] = look.cover;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== look.visibility) {
    t3 = look.visibility === "public" && <span className="absolute top-2 left-2 rounded-full bg-white/90 px-2 py-0.5 text-[11px] leading-4 font-semibold text-ds-primary">По ссылке</span>;
    $[2] = look.visibility;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = <span className="relative aspect-[3/4] overflow-hidden rounded-[12px] bg-white">{t2}{t3}</span>;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== look.title) {
    t5 = <span className="truncate text-[14px] leading-[18px] font-semibold text-ds-primary">{look.title}</span>;
    $[7] = look.title;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== look.productCount) {
    t6 = productCountLabel(look.productCount);
    $[9] = look.productCount;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t6) {
    t7 = <span className="text-[12px] leading-4 text-ds-secondary">{t6}</span>;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t5 || $[14] !== t7) {
    t8 = <span className="flex min-w-0 flex-col gap-0.5">{t5}{t7}</span>;
    $[13] = t5;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t1 || $[17] !== t4 || $[18] !== t8) {
    t9 = <Link to={t1} className="group flex min-w-0 flex-col gap-2 rounded-[12px] outline-none focus-visible:ring-2 focus-visible:ring-ds-focus">{t4}{t8}</Link>;
    $[16] = t1;
    $[17] = t4;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
}
export function ProfileLookPage() {
  const {
    lookId = ""
  } = useParams();
  const userId = useProfile(s => s.user?.id ?? "guest");
  const refresh = useLookCache();
  const navigate = useNavigate();
  const [renaming, setRenaming] = useState(false);
  const [busy, setBusy] = useState(false);
  const look = useQuery({
    queryKey: ["buyer", userId, "look", lookId],
    queryFn: () => api.looks.get(lookId),
    enabled: Boolean(lookId)
  });
  const productIds = look.data?.productIds ?? [];
  const items = useQuery({
    queryKey: ["buyer", userId, "look", lookId, "products", productIds.join(",")],
    enabled: productIds.length > 0,
    queryFn: async (): Promise<HistoryItem[]> => {
      const products = await api.products.byIds(productIds).catch(() => []);
      const byId = new Map(products.map(p => [p.id, p]));
      return productIds.flatMap(id => {
        const p_0 = byId.get(id);
        return p_0 ? [{
          productId: id,
          name: p_0.name,
          image: p_0.photos[0],
          price: p_0.price,
          oldPrice: p_0.oldPrice,
          product: p_0
        }] : [];
      });
    }
  });
  const run = async (action: () => Promise<Look | void>, done?: string) => {
    setBusy(true);
    try {
      const next = await action();
      refresh(next || undefined);
      if (done) toast.success(done);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Не удалось сохранить");
    } finally {
      setBusy(false);
    }
  };
  const crumbs = [{
    label: "Мои образы",
    to: "/profile/looks"
  }, {
    label: look.data?.title ?? "Образ"
  }];
  if (look.isError) {
    return <ProfilePanel crumbs={crumbs}>
        <CabinetEmpty title="Образ не найден" hint="Возможно, его удалили." />
      </ProfilePanel>;
  }
  const data = look.data;
  return <ProfilePanel crumbs={crumbs} actions={data ? <>
            <Button size="icon-sm" variant="ghost" aria-label="Переименовать" onClick={() => setRenaming(true)} data-testid="look-rename">
              <EditIcon size={16} />
            </Button>
            <Button size="icon-sm" variant="ghost" aria-label="Удалить образ" disabled={busy} data-testid="look-delete" onClick={() => {
      if (!window.confirm(`Удалить образ «${data.title}»?`)) return;
      void run(async () => {
        await api.looks.remove(data.id);
        navigate("/profile/looks", {
          replace: true
        });
      }, "Образ удалён");
    }}>
              <TrashIcon size={16} />
            </Button>
          </> : null}>
      {!data ? <Skeleton className="h-40 rounded-[12px]" /> : <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-3 rounded-[16px] bg-white p-3 sm:flex-row sm:items-center sm:justify-between">
            <label className="flex items-center gap-2.5 text-[14px] leading-[18px] font-medium text-ds-primary">
              <Switch checked={data.visibility === "public"} disabled={busy} data-testid="look-public" onCheckedChange={checked => void run(() => api.looks.update(data.id, {
            visibility: checked ? "public" : "private"
          }), checked ? "Образ открыт по ссылке" : "Образ закрыт")} />
              Доступ по ссылке
            </label>
            {data.visibility === "public" ? <Button size="sm" variant="gray" onClick={() => {
          void navigator.clipboard?.writeText(lookUrl(data)).then(() => toast.success("Ссылка скопирована")).catch(() => toast.error("Не удалось скопировать"));
        }}>
                <ShareIcon size={16} />
                Скопировать ссылку
              </Button> : <p className="text-[12px] leading-4 text-ds-secondary">Образ видите только вы</p>}
          </div>

          {productIds.length === 0 ? <CabinetEmpty title="В образе пока нет товаров" hint="Откройте товар и нажмите «В подборку»." action={<Button size="md" variant="dark" asChild>
                  <Link to="/catalog">В каталог</Link>
                </Button>} /> : items.isPending ? <div className={GRID}>
              {productIds.map(id_0 => <Skeleton key={id_0} className="aspect-[149/281] rounded-[4px]" />)}
            </div> : <div className={GRID} data-testid="look-products">
              {(items.data ?? []).map(item => <div key={item.productId} className="relative">
                  <HistoryCard item={item} />
                  <button type="button" aria-label={`Убрать «${item.name}» из образа`} disabled={busy} data-testid="look-remove-product" onClick={() => void run(() => api.looks.removeProduct(data.id, item.productId), "Товар убран из образа")} className="absolute top-1.5 left-1.5 grid size-7 place-items-center rounded-full bg-white/90 text-ds-primary shadow-sm outline-none hover:bg-white focus-visible:ring-2 focus-visible:ring-ds-focus disabled:opacity-60">
                    <XIcon size={16} />
                  </button>
                </div>)}
            </div>}
        </div>}
      <LookNameDialog key={renaming ? `rename-${data?.title ?? ""}` : "closed"} open={renaming} onClose={() => setRenaming(false)} initial={data?.title ?? ""} title="Переименовать образ" submitLabel="Сохранить" onSubmit={async title => {
      if (!data) return;
      refresh(await api.looks.update(data.id, {
        title
      }));
    }} />
    </ProfilePanel>;
}